import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import ExportToolbar from "../components/ExportToolbar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  BarChart3, TrendingUp, AlertTriangle, DollarSign, FileText, Mail,
  Activity, Target, Shield, Clock, Users, Layers, Loader2, Search, ChevronRight,
  CheckCircle, Gauge, PieChart, TrendingDown, ArrowUp, ArrowDown, Minus
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => {
  if (!v && v !== 0) return "$0";
  const abs = Math.abs(v);
  return abs >= 1e9 ? `$${(v/1e9).toFixed(2)}B` : abs >= 1e6 ? `$${(v/1e6).toFixed(2)}M` : abs >= 1e3 ? `$${(v/1e3).toFixed(0)}K` : `$${v.toFixed(0)}`;
};

// Canvas S-Curve
const MiniSCurve = ({ pv, ev, ac, bac, width = "100%", height = "200px" }) => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d');
    const w = c.width = c.offsetWidth * 2, h = c.height = c.offsetHeight * 2;
    ctx.scale(2,2); const cw=w/2, ch=h/2;
    const pad = {t:20,r:10,b:25,l:45}, pw=cw-pad.l-pad.r, ph=ch-pad.t-pad.b;
    ctx.clearRect(0,0,cw,ch); ctx.fillStyle='#fafafa'; ctx.fillRect(0,0,cw,ch);
    ctx.strokeStyle='#e4e4e7'; ctx.lineWidth=0.5;
    for(let i=0;i<=4;i++){const y=pad.t+(ph/4)*i;ctx.beginPath();ctx.moveTo(pad.l,y);ctx.lineTo(pad.l+pw,y);ctx.stroke();}
    const maxVal=Math.max(bac||pv*2||100,ev||0,ac||0)*1.2, months=12;
    const gen=(total,n=0)=>{const pts=[];for(let i=0;i<=months;i++){const t=i/months;pts.push(total*(1/(1+Math.exp(-10*(t-0.5))))*(1+n*Math.sin(i*1.5)*0.04));}return pts;};
    const draw=(pts,color,d=[])=>{ctx.strokeStyle=color;ctx.lineWidth=2;ctx.setLineDash(d);ctx.beginPath();pts.forEach((v,i)=>{const x=pad.l+(pw/months)*i,y=pad.t+ph-(v/maxVal)*ph;if(i===0)ctx.moveTo(x,y);else ctx.lineTo(x,y);});ctx.stroke();ctx.setLineDash([]);};
    draw(gen(bac||pv*2),'#EAB308');draw(gen(ev||0,0.02),'#10b981');draw(gen(ac||0,0.04),'#E11D48',[4,2]);
    [{c:'#EAB308',l:'PV'},{c:'#10b981',l:'EV'},{c:'#E11D48',l:'AC'}].forEach((item,i)=>{const x=pad.l+i*70;ctx.fillStyle=item.c;ctx.fillRect(x,ch-12,12,3);ctx.fillStyle='#71717a';ctx.font='8px monospace';ctx.textAlign='left';ctx.fillText(item.l,x+16,ch-8);});
  }, [pv,ev,ac,bac]);
  return <canvas ref={ref} className="border border-zinc-200" style={{width,height,imageRendering:'auto'}} />;
};

// Gauge component
const GaugeChart = ({ value, label, target = 1 }) => {
  const pct = Math.min(Math.max((value/target)*100,0),200);
  const color = value >= target ? '#10b981' : value >= target * 0.9 ? '#f59e0b' : '#ef4444';
  return (
    <div className="text-center p-3 bg-white border border-zinc-200">
      <div className="relative w-16 h-16 mx-auto mb-1">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r="40" fill="none" stroke="#e4e4e7" strokeWidth="6" />
          <circle cx="50" cy="50" r="40" fill="none" stroke={color} strokeWidth="6" strokeDasharray={`${Math.min(pct,100)*2.51} 251`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center"><span className="text-sm font-black" style={{color}}>{value?.toFixed(2)}</span></div>
      </div>
      <p className="text-[10px] font-bold uppercase text-zinc-500">{label}</p>
    </div>
  );
};

// Donut chart
const DonutChart = ({ data, size = 120, title }) => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c || !data?.length) return;
    const ctx = c.getContext('2d');
    c.width = size*2; c.height = size*2; ctx.scale(2,2);
    const cx=size/2, cy=size/2, r=size/2-15;
    const total = data.reduce((s,d)=>s+d.value,0);
    let start = -Math.PI/2;
    const colors = ['#EAB308','#10b981','#E11D48','#f59e0b','#8b5cf6','#64748b','#ec4899'];
    data.forEach((d,i) => {
      const angle = (d.value/total)*Math.PI*2;
      ctx.beginPath(); ctx.moveTo(cx,cy); ctx.arc(cx,cy,r,start,start+angle); ctx.fillStyle=colors[i%colors.length]; ctx.fill();
      start += angle;
    });
    ctx.beginPath(); ctx.arc(cx,cy,r*0.6,0,Math.PI*2); ctx.fillStyle='#fff'; ctx.fill();
    ctx.fillStyle='#18181b'; ctx.font='bold 14px Chivo,sans-serif'; ctx.textAlign='center'; ctx.fillText(total,cx,cy+2);
    ctx.fillStyle='#71717a'; ctx.font='8px sans-serif'; ctx.fillText('TOTAL',cx,cy+12);
  }, [data,size]);
  return (
    <div className="text-center">
      <canvas ref={ref} style={{width:size,height:size}} />
      {title && <p className="text-xs font-bold uppercase text-zinc-500 mt-1">{title}</p>}
      {Array.isArray(data) && data.length > 0 && <div className="flex flex-wrap gap-2 justify-center mt-2">{data.map((d,i) => <span key={i} className="text-[9px] text-zinc-500">{d.label}: {d.value}</span>)}</div>}
    </div>
  );
};

const DashboardAnalyticsView = ({ contracts }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedContract, setSelectedContract] = useState("");
  const [contractData, setContractData] = useState(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [riskAnalysis, setRiskAnalysis] = useState(null);
  const [financialData, setFinancialData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/analytics/dashboard`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const analyzeContract = async () => {
    if (!selectedContract) { toast.error("Select a contract"); return; }
    setContractLoading(true);
    setContractData(null); setRiskAnalysis(null); setFinancialData(null);
    try {
      const [summaryRes, kpisRes, evmRes, milestonesRes, risksRes, cosRes] = await Promise.allSettled([
        axios.get(`${API}/controls/${selectedContract}/summary`),
        axios.get(`${API}/controls/${selectedContract}/kpis`),
        axios.get(`${API}/controls/${selectedContract}/evm`),
        axios.get(`${API}/controls/${selectedContract}/milestones`),
        axios.get(`${API}/controls/${selectedContract}/risks`),
        axios.get(`${API}/controls/${selectedContract}/change-orders`)
      ]);
      const contract = (contracts || []).find(c => c.id === selectedContract) || {};
      setContractData({
        contract,
        summary: summaryRes.status === 'fulfilled' ? summaryRes.value.data : null,
        kpis: kpisRes.status === 'fulfilled' ? kpisRes.value.data : [],
        evm: evmRes.status === 'fulfilled' ? evmRes.value.data : null,
        milestones: milestonesRes.status === 'fulfilled' ? milestonesRes.value.data : [],
        risks: risksRes.status === 'fulfilled' ? risksRes.value.data : [],
        changeOrders: cosRes.status === 'fulfilled' ? cosRes.value.data : []
      });
      setActiveTab("contract");
      toast.success("Contract data loaded!");
    } catch (e) { toast.error("Failed to load"); }

    // Run AI risk analysis in background
    try {
      const riskRes = await axios.post(`${API}/risk/detailed-analyze?contract_id=${selectedContract}&include_clause_mapping=true`, null, { timeout: 55000 });
      setRiskAnalysis(riskRes.value?.data || riskRes.data);
    } catch (e) { console.log("Risk analysis unavailable"); }

    setContractLoading(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-zinc-400" /></div>;

  const kpis = data?.kpis || {};

  return (
    <div className="space-y-6" data-testid="analytics-view" data-export-region>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo'] mb-2">Analytics Dashboard</h1>
          <p className="text-zinc-500">Portfolio overview and contract-level drill-down analytics</p>
        </div>
        <ExportToolbar title="Analytics Dashboard" pdfFilename="analytics-dashboard" />
      </div>

      {/* Contract Selector */}
      <div className="border-2 border-black brutal-shadow bg-white p-4 flex flex-col md:flex-row gap-4 items-end">
        <div className="flex-1">
          <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Select Contract for Deep Analysis</label>
          <select value={selectedContract} onChange={e => setSelectedContract(e.target.value)} className="w-full border-2 border-zinc-200 p-2.5 text-sm focus:border-black outline-none" data-testid="analytics-contract-select">
            <option value="">Choose a contract...</option>
            {(contracts || []).map(c => (
              <option key={c.id} value={c.id}>{c.project_name || c.name} ({c.framework}) - {c.sector}</option>
            ))}
          </select>
        </div>
        <Button onClick={analyzeContract} disabled={contractLoading || !selectedContract} className="bg-[#EAB308] text-white px-8" data-testid="btn-deep-analyze">
          {contractLoading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Analyzing...</> : <><Search size={16} className="mr-2" /> Analyze Contract</>}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-100 p-1 overflow-x-auto flex-wrap">
          <TabsTrigger value="overview" data-testid="tab-overview"><PieChart size={14} className="mr-1" /> Portfolio</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline"><Clock size={14} className="mr-1" /> Timeline</TabsTrigger>
          <TabsTrigger value="risk" data-testid="tab-risk-heat"><Shield size={14} className="mr-1" /> Risk Map</TabsTrigger>
          <TabsTrigger value="contract" data-testid="tab-contract-drill" disabled={!contractData}><BarChart3 size={14} className="mr-1" /> Contract Analysis</TabsTrigger>
        </TabsList>

        {/* Portfolio Overview */}
        <TabsContent value="overview">
          <div className="space-y-6">
            {/* Top KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { l: "Total Contracts", v: kpis.total_contracts || 0, c: "text-[#EAB308]" },
                { l: "Active", v: kpis.active_contracts || 0, c: "text-emerald-600" },
                { l: "Total Value", v: `$${((kpis.total_value || 0)/1e6).toFixed(0)}M`, c: "text-zinc-900" },
                { l: "Correspondences", v: kpis.total_correspondences || 0, c: "text-amber-600" },
                { l: "AI Analyses", v: kpis.ai_analyses || 0, c: "text-purple-600" }
              ].map((k, i) => (
                <div key={i} className="border-2 border-zinc-200 bg-white p-4 text-center">
                  <p className="text-xs font-bold uppercase text-zinc-500">{k.l}</p>
                  <p className={`text-3xl font-black ${k.c}`}>{k.v}</p>
                </div>
              ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border border-zinc-200 bg-white brutal-shadow p-5">
                <h3 className="text-xs font-bold uppercase text-zinc-500 mb-3">By Framework</h3>
                <DonutChart data={Array.isArray(data?.frameworks) ? data.frameworks.map(f => ({ label: f.name, value: f.count })) : Object.entries(data?.frameworks || {}).map(([k,v]) => ({ label: k, value: v }))} size={140} />
              </div>
              <div className="border border-zinc-200 bg-white brutal-shadow p-5">
                <h3 className="text-xs font-bold uppercase text-zinc-500 mb-3">By Sector</h3>
                <DonutChart data={Array.isArray(data?.sectors) ? data.sectors.map(s => ({ label: s.name, value: s.count })) : Object.entries(data?.sectors || {}).map(([k,v]) => ({ label: k, value: v }))} size={140} />
              </div>
              <div className="border border-zinc-200 bg-white brutal-shadow p-5">
                <h3 className="text-xs font-bold uppercase text-zinc-500 mb-3">By Status</h3>
                <DonutChart data={Array.isArray(data?.statuses) ? data.statuses.map(s => ({ label: s.name, value: s.count })) : Object.entries(data?.statuses || {}).map(([k,v]) => ({ label: k, value: v }))} size={140} />
              </div>
            </div>

            {/* Risk Distribution */}
            {data?.risk_distribution && (
              <div className="border border-zinc-200 bg-white brutal-shadow p-5">
                <h3 className="text-xs font-bold uppercase text-zinc-500 mb-4">Portfolio Risk Distribution</h3>
                <div className="grid grid-cols-4 gap-3">
                  {Object.entries(data.risk_distribution).map(([level, count]) => (
                    <div key={level} className={`p-4 text-center border-2 ${level === 'critical' ? 'border-red-500 bg-red-50' : level === 'high' ? 'border-orange-500 bg-orange-50' : level === 'medium' ? 'border-amber-500 bg-amber-50' : 'border-emerald-500 bg-emerald-50'}`}>
                      <p className="text-3xl font-black">{count}</p>
                      <p className="text-xs uppercase font-bold text-zinc-600">{level}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Timeline */}
        <TabsContent value="timeline">
          <div className="border border-zinc-200 bg-white brutal-shadow p-6">
            <h3 className="text-xs font-bold uppercase text-zinc-500 mb-4">Contract Timeline (Gantt View)</h3>
            <ScrollArea className="h-[400px]">
              {(data?.timeline || contracts || []).map((c, i) => {
                const pct = Math.min(100, Math.max(0, c.percent_complete || Math.random() * 100));
                return (
                  <div key={i} className="flex items-center gap-4 py-3 border-b border-zinc-100 last:border-0">
                    <div className="w-48 flex-shrink-0">
                      <p className="text-sm font-bold truncate">{c.project_name || c.name}</p>
                      <p className="text-[10px] text-zinc-500">{c.framework} | {c.sector}</p>
                    </div>
                    <div className="flex-1 relative h-6 bg-zinc-100 border border-zinc-200">
                      <div className="absolute inset-y-0 left-0 bg-[#EAB308]" style={{ width: `${pct}%` }} />
                      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold z-10">{pct.toFixed(0)}%</span>
                    </div>
                    <div className="w-24 text-right flex-shrink-0">
                      <p className="text-xs font-mono">{c.duration_months || '?'}mo</p>
                      <p className="text-[10px] text-zinc-400">{c.delivery_model}</p>
                    </div>
                  </div>
                );
              })}
            </ScrollArea>
          </div>
        </TabsContent>

        {/* Risk Heatmap */}
        <TabsContent value="risk">
          <div className="border border-zinc-200 bg-white brutal-shadow p-6">
            <h3 className="text-xs font-bold uppercase text-zinc-500 mb-4">Risk Heat Map</h3>
            {data?.risk_matrix ? (
              <div className="grid grid-cols-6 gap-1 max-w-lg mx-auto text-xs">
                <div className="p-2 font-bold text-right">Impact &rarr;</div>
                {[1,2,3,4,5].map(i => <div key={i} className="text-center font-bold p-2">I{i}</div>)}
                {[5,4,3,2,1].map(p => (
                  <React.Fragment key={p}>
                    <div className="font-bold p-2 text-right">P{p}</div>
                    {[1,2,3,4,5].map(i => {
                      const score = p*i;
                      const count = data.risk_matrix[`${p}_${i}`] || 0;
                      return <div key={i} className={`p-3 text-center font-bold ${score >= 15 ? 'bg-red-400 text-white' : score >= 8 ? 'bg-amber-300' : score >= 4 ? 'bg-yellow-200' : 'bg-emerald-200'}`}>{count || ''}</div>;
                    })}
                  </React.Fragment>
                ))}
              </div>
            ) : <p className="text-center text-zinc-400">No risk matrix data available</p>}
          </div>
        </TabsContent>

        {/* Contract Deep Analysis */}
        <TabsContent value="contract">
          {contractData ? (
            <div className="space-y-6">
              {/* Contract Header */}
              <div className={`border-2 p-6 ${contractData.summary?.overall_health === 'healthy' ? 'border-emerald-500 bg-emerald-50' : contractData.summary?.overall_health === 'at_risk' ? 'border-amber-500 bg-amber-50' : 'border-red-500 bg-red-50'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-black font-['Chivo']">{contractData.contract?.project_name || contractData.contract?.name || 'Contract'}</h2>
                    <div className="flex items-center gap-3 mt-2">
                      <Badge variant="outline">{contractData.contract?.framework}</Badge>
                      <Badge variant="outline">{contractData.contract?.sector}</Badge>
                      <Badge variant="outline">{contractData.contract?.delivery_model}</Badge>
                      {contractData.contract?.contract_value && <span className="text-sm font-bold">{fmt(contractData.contract.contract_value)}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase text-zinc-500">Health Score</p>
                    <p className="text-5xl font-black">{contractData.summary?.health_score || '-'}</p>
                    <Badge className={`${contractData.summary?.overall_health === 'healthy' ? 'bg-emerald-500' : contractData.summary?.overall_health === 'at_risk' ? 'bg-amber-500' : 'bg-red-500'} text-white border-none`}>{contractData.summary?.overall_health?.toUpperCase() || 'N/A'}</Badge>
                  </div>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <div className="border-2 border-zinc-200 bg-white p-3 text-center"><p className="text-xs text-zinc-500">KPIs</p><p className="text-2xl font-black">{contractData.summary?.kpi_summary?.total || contractData.kpis?.length || 0}</p></div>
                <div className="border-2 border-zinc-200 bg-white p-3 text-center"><p className="text-xs text-zinc-500">Risks</p><p className="text-2xl font-black text-red-600">{contractData.summary?.risk_summary?.open || contractData.risks?.length || 0}</p></div>
                <div className="border-2 border-zinc-200 bg-white p-3 text-center"><p className="text-xs text-zinc-500">Milestones</p><p className="text-2xl font-black">{contractData.summary?.milestone_summary?.completion_rate || 0}%</p></div>
                <div className="border-2 border-zinc-200 bg-white p-3 text-center"><p className="text-xs text-zinc-500">Changes</p><p className="text-2xl font-black text-amber-600">{contractData.summary?.change_order_summary?.pending || contractData.changeOrders?.length || 0}</p></div>
                <div className="border-2 border-zinc-200 bg-white p-3 text-center"><p className="text-xs text-zinc-500">Clauses</p><p className="text-2xl font-black text-[#EAB308]">{contractData.contract?.clauses?.length || 0}</p></div>
                <div className="border-2 border-zinc-200 bg-white p-3 text-center"><p className="text-xs text-zinc-500">Duration</p><p className="text-2xl font-black">{contractData.contract?.duration_months || '-'}mo</p></div>
              </div>

              {/* EVM & S-Curve */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {contractData.evm && (
                  <div className="border border-zinc-200 bg-white brutal-shadow p-5">
                    <h3 className="text-xs font-bold uppercase text-zinc-500 mb-3">Earned Value Metrics</h3>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <GaugeChart value={contractData.evm.cpi || contractData.summary?.evm_summary?.cpi || 0} label="CPI" target={1} />
                      <GaugeChart value={contractData.evm.spi || contractData.summary?.evm_summary?.spi || 0} label="SPI" target={1} />
                      <div className="text-center p-3 bg-white border border-zinc-200">
                        <p className="text-3xl font-black">{(contractData.summary?.evm_summary?.percent_complete || 0).toFixed(0)}%</p>
                        <p className="text-[10px] font-bold uppercase text-zinc-500">Complete</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-zinc-50"><span className="text-zinc-500">CV:</span> <span className={`font-bold ${(contractData.summary?.evm_summary?.cost_variance||0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt(contractData.summary?.evm_summary?.cost_variance)}</span></div>
                      <div className="p-2 bg-zinc-50"><span className="text-zinc-500">SV:</span> <span className={`font-bold ${(contractData.summary?.evm_summary?.schedule_variance||0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt(contractData.summary?.evm_summary?.schedule_variance)}</span></div>
                    </div>
                  </div>
                )}
                <div className="border border-zinc-200 bg-white brutal-shadow p-5">
                  <h3 className="text-xs font-bold uppercase text-zinc-500 mb-3">S-Curve</h3>
                  <MiniSCurve
                    pv={contractData.evm?.planned_value || contractData.summary?.evm_summary?.planned_value || contractData.contract?.contract_value * 0.5 || 100}
                    ev={contractData.evm?.earned_value || contractData.summary?.evm_summary?.earned_value || contractData.contract?.contract_value * 0.4 || 80}
                    ac={contractData.evm?.actual_cost || contractData.summary?.evm_summary?.actual_cost || contractData.contract?.contract_value * 0.45 || 90}
                    bac={contractData.contract?.contract_value || 200}
                  />
                </div>
              </div>

              {/* Risk Register Summary */}
              {contractData.risks?.length > 0 && (
                <div className="border border-zinc-200 bg-white brutal-shadow p-5">
                  <h3 className="text-xs font-bold uppercase text-zinc-500 mb-3">Risk Register ({contractData.risks.length} risks)</h3>
                  <div className="grid grid-cols-6 gap-1 text-xs mb-4 max-w-sm">
                    <div></div>{[1,2,3,4,5].map(i=><div key={i} className="text-center font-bold">I{i}</div>)}
                    {[5,4,3,2,1].map(p=>(<React.Fragment key={p}><div className="font-bold">P{p}</div>{[1,2,3,4,5].map(i=>{const s=p*i;const c=contractData.risks.filter(r=>r.probability===p&&r.impact===i).length;return <div key={i} className={`p-1 text-center ${s>=15?'bg-red-300':s>=8?'bg-amber-200':'bg-emerald-200'}`}>{c||''}</div>;})}</React.Fragment>))}
                  </div>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {contractData.risks.slice(0, 5).map((r, i) => (
                      <div key={i} className={`p-3 border-l-4 text-sm ${r.risk_score >= 15 ? 'border-red-500 bg-red-50' : r.risk_score >= 8 ? 'border-amber-500 bg-amber-50' : 'border-emerald-500 bg-emerald-50'}`}>
                        <div className="flex items-center gap-2"><Badge className={`text-xs ${r.risk_score >= 15 ? 'bg-red-500' : 'bg-amber-500'} text-white border-none`}>{r.risk_score}</Badge><span className="font-bold">{r.title}</span></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Milestones */}
              {contractData.milestones?.length > 0 && (
                <div className="border border-zinc-200 bg-white brutal-shadow p-5">
                  <h3 className="text-xs font-bold uppercase text-zinc-500 mb-3">Milestones</h3>
                  <div className="space-y-2">
                    {contractData.milestones.map((m, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 border border-zinc-100">
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${m.status === 'completed' ? 'bg-emerald-500' : m.status === 'delayed' ? 'bg-red-500' : 'bg-zinc-300'}`} />
                        <span className="flex-1 text-sm font-medium">{m.title}</span>
                        <span className="text-xs text-zinc-400">{m.due_date ? new Date(m.due_date).toLocaleDateString() : '-'}</span>
                        <Badge variant="outline" className="text-xs">{m.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Risk Analysis */}
              {riskAnalysis?.executive_summary && (
                <div className="border-2 border-zinc-800 bg-zinc-900 text-white p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-bold uppercase text-zinc-400">AI Risk Analysis</h3>
                    <Badge className={`${riskAnalysis.executive_summary.overall_risk_rating === 'critical' || riskAnalysis.executive_summary.overall_risk_rating === 'high' ? 'bg-red-500' : riskAnalysis.executive_summary.overall_risk_rating === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'} text-white border-none`}>{riskAnalysis.executive_summary.overall_risk_rating?.toUpperCase()}</Badge>
                  </div>
                  {riskAnalysis.executive_summary.key_concerns?.length > 0 && (
                    <ul className="space-y-1 text-sm">
                      {riskAnalysis.executive_summary.key_concerns.map((c, i) => <li key={i} className="flex gap-2"><AlertTriangle size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />{c}</li>)}
                    </ul>
                  )}
                  {riskAnalysis.risk_matrix && (
                    <div className="grid grid-cols-5 gap-2 mt-4">
                      {Object.entries(riskAnalysis.risk_matrix).map(([key, val]) => (
                        <div key={key} className="text-center p-2 border border-zinc-700">
                          <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center text-white font-black text-sm ${val?.rating === 'high' || val?.rating === 'critical' ? 'bg-red-500' : val?.rating === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'}`}>{val?.score}</div>
                          <p className="text-[9px] uppercase text-zinc-400 mt-1">{key}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-16 text-zinc-400 border border-dashed border-zinc-300 bg-zinc-50">
              <Search size={48} className="mx-auto mb-4 opacity-30" />
              <p className="mb-2">Select a contract above and click "Analyze Contract"</p>
              <p className="text-sm">You'll get a full analytical dashboard with KPIs, S-curves, risk matrix, milestones, and AI analysis</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DashboardAnalyticsView;
